import { GraduationCap, BookOpen, type LucideIcon } from "lucide-react";
import type { Role } from "@/services/UserManagmentServices";

/**
 * Shared presentation config for the two switchable roles.
 * Single source of truth for labels, icons, colours, copy and destinations
 * used by the account switcher (and anywhere else that renders a role).
 */
export interface RoleDisplay {
  /** Full label shown in the switcher, e.g. "Teacher mode". */
  label: string;
  /** Short noun, e.g. "Teacher". */
  short: string;
  icon: LucideIcon;
  /** One-line description of what the mode does. */
  description: string;
  /** Description used on the "Set up … mode" (acquire) row. */
  addDescription: string;
  /** Dashboard landing route for this role. */
  dashboard: string;
  /** Tailwind classes for the coloured icon tile (idle state). */
  iconTile: string;
  /** Tailwind classes for the current-mode highlight (row background + border). */
  highlight: string;
  /** Tailwind text colour for the accent (check mark / "Current" tag). */
  accentText: string;
}

export const ROLE_DISPLAY: Record<Role, RoleDisplay> = {
  TEACHER: {
    label: "Teacher mode",
    short: "Teacher",
    icon: GraduationCap,
    description: "Teach, manage offerings & inquiries",
    addDescription: "Build a profile and publish offerings",
    dashboard: "/teacher-dashboard",
    iconTile:
      "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900",
    highlight:
      "bg-indigo-50 border-indigo-200 dark:bg-indigo-950/40 dark:border-indigo-900",
    accentText: "text-indigo-700 dark:text-indigo-300",
  },
  STUDENT: {
    label: "Student mode",
    short: "Student",
    icon: BookOpen,
    description: "Find teachers, book & track sessions",
    addDescription: "Discover teachers and book sessions",
    dashboard: "/student-dashboard",
    iconTile:
      "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:border-violet-900",
    highlight:
      "bg-violet-50 border-violet-200 dark:bg-violet-950/40 dark:border-violet-900",
    accentText: "text-violet-700 dark:text-violet-300",
  },
};

/**
 * Where to send the user to collect the info required for a role they don't yet
 * have. The role itself is created only once they submit the first required
 * piece of info on these pages (see `useSwitchRole.ensureRole`), so abandoning
 * the flow leaves no half-created account.
 */
export const ROLE_ONBOARDING_ROUTE: Record<Role, string> = {
  TEACHER: "/teacher-profile-setup",
  STUDENT: "/goal-onboarding",
};

/** The role the user would switch *to* from the given role. */
export function otherRole(role: Role): Role {
  return role === "TEACHER" ? "STUDENT" : "TEACHER";
}

/** Initials for the avatar fallback, derived from a display name or email. */
export function initialsFrom(name?: string | null, email?: string | null): string {
  const source = (name && name.trim()) || (email ? email.split("@")[0] : "");
  if (!source) return "?";
  const parts = source.replace(/[._-]+/g, " ").trim().split(/\s+/);
  const letters = parts.slice(0, 2).map((p) => p[0]).join("");
  return letters.toUpperCase() || "?";
}
